import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator } from "@frui.ts/validation";
import { Modules } from "config";
import RefundType from "entities/refundType";
import TransferType from "entities/transferType";
import type PayGateToTransferRefundResponse from "entities/payGateToTransferRefundResponse";
import PayGateToTransferRefundsRequest, {
  PayGateToTransferRefundsRequestValidationRules,
} from "entities/payGateToTransferRefundsRequest";
import { computed, reaction } from "mobx";
import RefundRepository from "repositories/refundRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import RefundsApprovalService from "services/refundsApprovalService";
import OptionsService from "services/optionsService";
import PayGateToTransferRefundPaymentTemplate from "../../../../entities/payGateToTransferRefundPaymentTemplate";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import PayGateToTransferRefundDetailViewModel from "./payGateToTransferRefundDetailViewModel";
import RefundApprovalType from "entities/refundApprovalType";
import RefundApprovalsListViewModel from "../refundApprovalsListViewModel";
import Selectable, { SelectableListViewModel } from "../../../selectableListViewModel";
import { emptyValue } from "utils/emptyValue";

const navigationName = "paygate-to-transfer";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [PayGateToTransferRefundDetailViewModel],
})
@Selectable
export default class PayGateToTransferRefundsViewModel extends RefundApprovalsListViewModel<
  PayGateToTransferRefundResponse,
  PayGateToTransferRefundDetailViewModel,
  PayGateToTransferRefundsRequest
> {
  readonly modelName = "payGateToTransferRefund";
  readonly refundType = RefundType.PayGateToTransfer;
  readonly approvalType = RefundApprovalType;

  @computed
  get refundTransferTypeOptions() {
    return this.optionsService.internalEnumOptions(TransferType);
  }

  @computed
  get payGateToTransferRefundPaymentTemplatesOptions() {
    return this.optionsService.entityOptions(PayGateToTransferRefundPaymentTemplate);
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof PayGateToTransferRefundDetailViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected repository: RefundRepository,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(localizationService, eventBus, approvalService, countrySelectViewModelFactory);
    this.navigationName = navigationName;
  }

  @computed
  get self(): SelectableListViewModel<PayGateToTransferRefundsViewModel, PayGateToTransferRefundResponse> {
    return this as any;
  }

  protected processData(items: PayGateToTransferRefundResponse[]) {
    items.forEach(item => {
      item.isSelectable =
        item.permittedActions.approval.isAllowed && (this.canApprove(item) || false) && this.myApprovalStep(item);

      return item;
    });

    return items;
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.officialRecordId,
          this.filter.refundPaymentTemplateId,
          this.filter.refundAmount,
          this.filter.transferType,
          this.filter.countryId,
          this.filter.licensePlate,
          this.filter.terminalId,
          this.filter.authorizedDate,
          this.filter.variableSymbol,
          this.filter.invoiceNumber,
          this.filter.paidAmount,
          this.filter.cardNumber,
          this.filter.arn,
          this.filter.payGatePaymentIdent,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      )
    );
    return super.onInitialize();
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;

    this.repository
      .getAllPayGateToTransferRefund({ limit: 1, offset: 0 }, { approvedBy: RefundApprovalType.ChiefAccountant })
      .then(response => {
        if (response.items.length > 0) {
          this.isAnyExportable = true;
        }
      });

    return this.repository.getAllPayGateToTransferRefund({ offset, limit }, this.appliedFilter);
  }

  protected createFilter() {
    return attachAutomaticValidator(new PayGateToTransferRefundsRequest(), PayGateToTransferRefundsRequestValidationRules);
  }

  @bound
  onItemClick() {
    return (item: PayGateToTransferRefundResponse) =>
      this.tryActivateChild(this.detailViewModelFactory(item.id, this.pagingFilter));
  }

  async findNavigationChild(navigationName?: string) {
    if (navigationName && this.allowWrite) {
      return this.detailViewModelFactory(navigationName, this.pagingFilter);
    }

    return Promise.resolve(undefined);
  }

  @bound
  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.payGateToTransfer.${code}`, size);
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService
        .entityOptions(PayGateToTransferRefundPaymentTemplate)
        .find(option => option.id === refundPaymentTemplateId)?.label ?? emptyValue
    );
  }
}
